import { ulid } from 'ulid';
import type {
	CreateTerminalInput,
	UpdateTerminalInput,
	TerminalViewModel
} from '@/graphql/generated';

export interface GateFormSubmitData {
	terminalId?: string;
	name: string;
	ipAddress: string;
	gateKey?: string | null;
	terminalTypeId: string;
	isActive: boolean;
}

const normaliseString = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : null;
};

const normaliseTerminalTypeId = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

export const buildCreateGateInput = (
	formData: GateFormSubmitData,
	idempotentTerminalId: string
): CreateTerminalInput => {
	const terminalTypeId = normaliseTerminalTypeId(formData.terminalTypeId);
	
	if (terminalTypeId === null) {
		throw new Error('Terminal type is required');
	}

	const input: CreateTerminalInput = {
		terminalId: idempotentTerminalId,
		name: formData.name.trim(),
		ipAddress: formData.ipAddress.trim(),
		terminalTypeId,
		isActive: formData.isActive
	};

	// Handle optional gateKey
	const gateKey = normaliseString(formData.gateKey);
	if (gateKey !== null) {
		input.gateKey = gateKey;
	}

	return input;
};

export const buildUpdateGateInput = (
	formData: GateFormSubmitData,
	terminalId: string
): UpdateTerminalInput => {
	const terminalTypeId = normaliseTerminalTypeId(formData.terminalTypeId);
	
	if (terminalTypeId === null) {
		throw new Error('Terminal type is required');
	}

	const input: UpdateTerminalInput = {
		terminalId,
		name: formData.name.trim(),
		ipAddress: formData.ipAddress.trim(),
		terminalTypeId,
		isActive: formData.isActive
	};

	// Handle optional gateKey
	const gateKey = normaliseString(formData.gateKey);
	if (gateKey !== null) {
		input.gateKey = gateKey;
	}

	return input;
};

export const mapGateToFormData = (
	terminal: TerminalViewModel
): GateFormSubmitData => {
	// Determine terminalTypeId
	let terminalTypeId = '';
	if (terminal.terminalTypeId) {
		terminalTypeId = String(terminal.terminalTypeId);
	} else if (terminal.terminalType) {
		terminalTypeId = String(terminal.terminalType.terminalTypeId);
	}

	return {
		terminalId: terminal.terminalId,
		name: terminal.name ?? '',
		ipAddress: terminal.ipAddress ?? '',
		gateKey: terminal.gateKey ?? null,
		terminalTypeId,
		isActive: terminal.isActive
	};
};

export const generateIdempotentGateId = (): string => {
	return ulid();
};
